import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { API_PATHS } from '../config/api-paths';
import { DebtorCandidate, DebtorDirectoryApi } from '../tokens/debtor-directory.token';

/** Search envelope — the resource wraps the rows in `{ data: [...] }`. */
interface DebtorSearchEnvelope {
  data: DebtorCandidate[];
}

/** The backend's own minimum query length — mirrored so a too-short query costs no request at all. */
const MIN_QUERY_LENGTH = 3;

/**
 * REST-only implementation of {@link DebtorDirectoryApi} (EM-1750, story EM-1671).
 *
 * Thin `HttpClient` wrapper over the live `DebtorDirectoryResource` at
 * `/services/embedded/api/v1/financing/debtors`. No mock, no fallback (Rules 07/10); the three-source
 * union and the ONRC projection are the backend's job — this service adds no parsing of its own
 * (Rule 17). Rides the Keycloak bearer via `authInterceptor`.
 *
 * The one client-side rule here is the short-circuit below: the backend already answers `[]` for a
 * query under three characters, and issuing that request anyway would put one HTTP call per keystroke
 * on the wire for no possible result.
 */
@Injectable()
export class DebtorDirectoryRestService implements DebtorDirectoryApi {
  private readonly http = inject(HttpClient);

  search(query: string, product?: string, limit?: number): Observable<DebtorCandidate[]> {
    const term = (query ?? '').trim();
    if (term.length < MIN_QUERY_LENGTH) {
      return of([]);
    }
    let params = new HttpParams().set('q', term);
    if (product) params = params.set('product', product);
    if (limit) params = params.set('limit', String(limit));
    return this.http
      .get<DebtorSearchEnvelope>(`${API_PATHS.FINANCING_DEBTORS}/search`, { params })
      .pipe(map(env => env?.data ?? []));
  }

  resolve(cui: string, product?: string): Observable<DebtorCandidate> {
    let params = new HttpParams();
    if (product) params = params.set('product', product);
    return this.http.get<DebtorCandidate>(
      `${API_PATHS.FINANCING_DEBTORS}/${encodeURIComponent((cui ?? '').trim())}/resolve`,
      { params },
    );
  }
}
